import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Icon from '../primitives/Icon';
import { cn } from '../../utils/cn';
import Button from '../primitives/Button';

const navLinks = [
  { label: 'About', href: '/about' },
  { label: 'AI Tools', href: '/ai-tools' },
  { label: 'AI Policies', href: '/ai-policies' },
];

const initiativeLinks = [
  { label: 'AI Literacy Uplift', href: '/initiative-one/ai-literacy-uplift-and-alignment' },
  { label: 'Build Platform', href: '/initiative-two/build-platform' },
  { label: 'Companion Incubator', href: '/initiative-three/companion-incubator' },
  { label: 'Ecosystem', href: '/initiatives/ecosystem' },
];

const Header: React.FC = () => {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isVisible, setIsVisible] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      
      // Always show header when at the very top (first 50px)
      if (currentScrollY <= 50) {
        setIsVisible(true);
        setLastScrollY(currentScrollY);
        return;
      }
      
      // Show header when scrolling up, hide when scrolling down
      if (currentScrollY > lastScrollY && currentScrollY > 100) {
        setIsVisible(false);
      } else {
        setIsVisible(true);
      }
      
      setLastScrollY(currentScrollY);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [lastScrollY]);

  return (
    <header className={cn(
      "w-full bg-gray-900 text-white sticky top-0 z-50 transition-transform duration-300",
      isVisible ? "translate-y-0" : "-translate-y-full"
    )}>
      {/* Return to The Hub strip */}
      <div className="w-full bg-black text-white py-2 px-4 text-center text-sm">
        <a href="https://thehub.ie.com.au/" className="font-semibold hover:underline">
          &larr; Return to The Hub
        </a>
      </div>

      {/* Main navigation */}
      <nav className="max-w-7xl mx-auto px-4 py-4">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center">
            <img
              src="/logos/ai-Lab.svg"
              alt="AI Lab logo"
              className="h-7 w-auto"
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-4">
            {/* Initiatives Dropdown */}
            <div className="relative">
              <Button
                variant="secondary"
                size="sm"
                className="bg-transparent text-white border-white/30 hover:bg-white/10"
                onClick={() => setDropdownOpen(!dropdownOpen)}
                onBlur={() => setTimeout(() => setDropdownOpen(false), 150)}
              >
                Initiatives
                <Icon name="chevron-down" size={18} className="ml-1 text-white" />
              </Button>
              
              {dropdownOpen && (
                <div className="absolute top-full left-0 mt-2 w-48 bg-gray-800 border border-gray-600 rounded-lg shadow-lg z-50">
                  {initiativeLinks.map(link => (
                    <Link
                      key={link.label}
                      to={link.href}
                      className="block px-4 py-2 text-white hover:bg-gray-700 hover:text-teal-400 rounded-lg transition-colors"
                      onClick={() => setDropdownOpen(false)}
                    >
                      {link.label}
                    </Link>
                  ))}
                </div>
              )}
            </div>

            {/* Navigation Links */}
            {navLinks.map(link => (
              <Link key={link.label} to={link.href}>
                <Button
                  variant="secondary"
                  size="sm"
                  className="bg-transparent text-white border-white/30 hover:bg-white/10"
                >
                  {link.label}
                </Button>
              </Link>
            ))}
          </div>

          {/* Mobile Menu Button */}
          <div className="md:hidden">
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="p-2 text-white hover:bg-gray-800 rounded"
              aria-label="Toggle menu"
            >
              <Icon name={mobileMenuOpen ? "x" : "menu"} size={24} />
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden mt-4 pt-4 border-t border-gray-700">
            <div className="space-y-2">
              <div className="font-semibold text-gray-300 mb-2">Initiatives</div>
              {initiativeLinks.map(link => (
                <Link
                  key={link.label}
                  to={link.href}
                  className="block py-2 text-white hover:text-teal-400 transition-colors"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  {link.label}
                </Link>
              ))}
            </div>
            <div className="mt-4 pt-4 border-t border-gray-700">
              {navLinks.map(link => (
                <Link
                  key={link.label}
                  to={link.href}
                  className="block py-2 text-white hover:text-teal-400 transition-colors"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  {link.label}
                </Link>
              ))}
            </div>
          </div>
        )}
      </nav>
    </header>
  );
};

export default Header;
